import type { Type } from '@angular/core';
import { InjectionToken } from '@angular/core';
import type { Nullable } from '@mosaik/core';
import { FunctionExtensions } from '@mosaik/core';
import { ReplaySubject } from 'rxjs';
import type { IPageSidebar } from './page-sidebar.model';
import type { PageTranslation } from './page-translation';

export interface ISidebarRef {
  locales: object;

  data?: object;

  confirm: () => void;

  cancel: () => void;
}

export const SIDEBAR_REF = new InjectionToken<ISidebarRef>('Sidebar ref');

export class PageSidebar implements IPageSidebar {
  private readonly _refChanged: ReplaySubject<void>;

  private _view: Nullable<Type<unknown>>;
  private _title: PageTranslation;
  private _isOpen: boolean;
  private _hasCancel: boolean;
  private _cancelCallback: () => void;
  private _cancelCallbackArgs: object;
  private _hasConfirm: boolean;
  private _confirmCallback: () => void;
  private _confirmCallbackArgs: object;
  private _ref: ISidebarRef;

  public constructor(value?: Partial<IPageSidebar>) {
    this._view = null;
    this._title = { value: '', translated: true };
    this._isOpen = false;
    this._hasCancel = false;
    this._cancelCallback = FunctionExtensions.noop;
    this._cancelCallbackArgs = {};
    this._hasConfirm = false;
    this._confirmCallback = FunctionExtensions.noop;
    this._confirmCallbackArgs = {};
    this._ref = { data: {}, locales: {}, confirm: FunctionExtensions.noop, cancel: FunctionExtensions.noop };
    this._refChanged = new ReplaySubject<void>(1);

    if (value) {
      Object.assign(this, value);
    }
  }

  public get view(): Nullable<Type<unknown>> {
    return this._view;
  }
  public set view(value: Nullable<Type<unknown>>) {
    this._view = value;
  }

  public get title(): PageTranslation {
    return this._title;
  }
  public set title(value: PageTranslation) {
    this._title = value;
  }

  public get isOpen(): boolean {
    return this._isOpen;
  }
  public set isOpen(value: boolean) {
    this._isOpen = value;
  }

  public get hasCancel(): boolean {
    return this._hasCancel;
  }
  public set hasCancel(value: boolean) {
    this._hasCancel = value;
  }

  public get cancelCallback(): () => void {
    return this._cancelCallback;
  }
  public set cancelCallback(value: () => void) {
    this._cancelCallback = value;
  }

  public get cancelCallbackArgs(): object {
    return this._cancelCallbackArgs;
  }
  public set cancelCallbackArgs(value: object) {
    this._cancelCallbackArgs = value;
  }

  public get hasConfirm(): boolean {
    return this._hasConfirm;
  }
  public set hasConfirm(value: boolean) {
    this._hasConfirm = value;
  }

  public get confirmCallback(): () => void {
    return this._confirmCallback;
  }
  public set confirmCallback(value: () => void) {
    this._confirmCallback = value;
  }

  public get confirmCallbackArgs(): object {
    return this._confirmCallbackArgs;
  }
  public set confirmCallbackArgs(value: object) {
    this._confirmCallbackArgs = value;
  }

  public get ref(): ISidebarRef {
    return this._ref;
  }
  public set ref(value: ISidebarRef) {
    this._ref = value;
    this._refChanged.next();
  }

  public get refChanged(): ReplaySubject<void> {
    return this._refChanged;
  }
}
